import React, { useEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import { auth, functions, httpsCallable } from "../../firebase";
import { fetchUserData, selectUser } from "features/userSlice";
import { Row, Spinner } from "reactstrap";
import { getCanvaRedirectUri } from "utils/canvaUtils";

export default function CanvaCallback() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const exchanged = useRef(false);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const state = params.get("state");

    if (error) {
      setErrorMsg(`Canva declined authorization: ${error}`);
      setStatus("error");
      return undefined;
    }

    if (!code || !state) {
      setErrorMsg("Canva did not return the information needed to complete the connection.");
      setStatus("error");
      return undefined;
    }

    const exchange = async (firebaseUser) => {
      if (exchanged.current) return;
      exchanged.current = true;

      try {
        const exchangeFn = httpsCallable(functions, "canvaExchangeToken");
        const redirectUri = getCanvaRedirectUri();

        await exchangeFn({ code, state, redirectUri });

        const uid = firebaseUser?.uid || user?.uid;
        if (uid) {
          await dispatch(fetchUserData(uid));
        }

        history.replace("/settings#canva");
      } catch (err) {
        console.error("[CanvaCallback] canvaExchangeToken error:", err);
        exchanged.current = false;
        setErrorMsg(err.message || "Failed to connect Canva. Please try again.");
        setStatus("error");
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        clearTimeout(authTimeout);
        unsubscribe();
        exchange(firebaseUser);
      }
    });

    const authTimeout = setTimeout(() => {
      unsubscribe();
      setErrorMsg(
        "Could not restore your session. For local testing, make sure you log in at http://127.0.0.1:3000 before connecting Canva."
      );
      setStatus("error");
    }, 8000);

    return () => {
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, [dispatch, history, location.search, user?.uid]);

  if (status === "loading") {
    return (
      <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "16px" }}>
        <Spinner style={{ height: "3rem", width: "3rem" }} color="info" />
        <p style={{ color: "#004aad", fontWeight: "bold" }}>Connecting your Canva account...</p>
      </Row>
    );
  }

  return (
    <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", gap: "12px", padding: "20px" }}>
      <i className="fa fa-exclamation-circle" style={{ fontSize: "48px", color: "#e74c3c" }} />
      <h4 style={{ color: "#004aad", fontWeight: "bold" }}>Canva Connection Failed</h4>
      <p style={{ color: "#666", textAlign: "center", maxWidth: "420px" }}>{errorMsg}</p>
      <button
        onClick={() => history.replace("/settings#canva")}
        style={{ marginTop: "8px", padding: "10px 24px", borderRadius: "30px", backgroundColor: "#00c4cc", color: "white", border: "none", cursor: "pointer", fontWeight: "bold" }}
      >
        Back to Settings
      </button>
    </Row>
  );
}
